import React, { useEffect, useRef, useState } from "react";
import { IStringLocalizer } from "../../../utilities/IStringLocalizer";
import { AiMetadataProperties } from "../../../store/Models/Surveys/SurveyAiMetadata";
import AIAnswerMetadataHeader from "./AIAnswerMetadataHeader";
import AIAnswerMetadataDetails from "./AIAnswerMetadataDetails";
import AIAnswerMetadataTopMessageComponent from "./AIAnswerMetadataTopMessage";
import { AiAnswerMetadataTopMessage, HelpfulSelection } from "./aiAnswerMetadata.types";
import {
    getConfidenceLabel,
    getConfidenceReasoning,
    hasAiMetadataContent,
    isInsufficientDataConfidenceLabel,
    isAiAnswerModified,
    mapAiSources,
    normalizeHelpfulSelection,
    toggleHelpfulSelection
} from "./aiAnswerMetadata.utils";
import "./styles.css";

interface AIAnswerMetadataProps {
    localizer: IStringLocalizer;
    aiMetadata: AiMetadataProperties | null | undefined;
    currentValue: string;
    isDisabled?: boolean;
    shouldShowModifiedState?: boolean;
    shouldShowRevertButton?: boolean;
    containerClassName?: string;
    detailsTopMessage?: AiAnswerMetadataTopMessage;
    onRevertToAiValue?: () => void;
    onHelpfulSelectionChange?: (isHelpful: -1 | 0 | 1) => void;
    onExpandedChange?: (isExpanded: boolean) => void;
    onModifiedStateChange?: (isModified: boolean) => void;
}

const AI_DETAILS_HIGHLIGHT_DURATION_MS = 450;

const AIAnswerMetadata: React.FC<AIAnswerMetadataProps> = ({
    localizer,
    aiMetadata,
    currentValue,
    isDisabled = false,
    shouldShowModifiedState = true,
    shouldShowRevertButton = true,
    containerClassName,
    detailsTopMessage,
    onRevertToAiValue,
    onHelpfulSelectionChange,
    onExpandedChange,
    onModifiedStateChange
}) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [isDetailsHighlighted, setIsDetailsHighlighted] = useState(false);
    const [helpfulSelection, setHelpfulSelection] = useState<HelpfulSelection>(0);
    const aiDetailsClickTimerRef = useRef<number | null>(null);

    useEffect(() => {
        setHelpfulSelection(normalizeHelpfulSelection(aiMetadata?.isHelpful));
    }, [aiMetadata?.isHelpful]);

    useEffect(() => {
        onExpandedChange?.(isExpanded);
    }, [isExpanded, onExpandedChange]);

    const reasoning = aiMetadata?.reasoning ?? "";
    const confidenceScoreDetails = aiMetadata?.confidenceScoreDetails;
    const sources = mapAiSources(aiMetadata);
    const confidenceLabel = getConfidenceLabel(confidenceScoreDetails);
    const confidenceReasoning = getConfidenceReasoning(confidenceScoreDetails);
    const hasAiMetadataToDisplay = hasAiMetadataContent(reasoning, sources, confidenceScoreDetails);
    const isInsufficientDataConfidence = !!confidenceScoreDetails
        && isInsufficientDataConfidenceLabel(confidenceLabel);
    const isModifiedValue = !!aiMetadata &&
        shouldShowModifiedState &&
        isAiAnswerModified(currentValue, aiMetadata.value, hasAiMetadataToDisplay);

    useEffect(() => {
        onModifiedStateChange?.(isModifiedValue);
    }, [isModifiedValue, onModifiedStateChange]);

    useEffect(() => {
        return () => {
            if (aiDetailsClickTimerRef.current !== null) {
                window.clearTimeout(aiDetailsClickTimerRef.current);
            }
        };
    }, []);

    if (!aiMetadata) {
        return null;
    }
    const isConfirmed = helpfulSelection === 1 && !isModifiedValue;
    const shouldRenderRevertButton = shouldShowRevertButton && aiMetadata.value.trim().length > 0;
    const wrapperClasses = [
        "ai-answer-field__combined-wrapper",
        containerClassName
    ].filter(className => !!className).join(" ");
    const containerClasses = [
        "ai-answer-field",
        isModifiedValue ? "ai-answer-field--modified" : "",
        isInsufficientDataConfidence ? "ai-answer-field--insufficient" : "",
        isConfirmed ? "ai-answer-field--confirmed" : ""
    ].filter(className => !!className).join(" ");

    if (!hasAiMetadataToDisplay) {
        return null;
    }

    const onToggleDetails = () => {
        setIsExpanded(previous => !previous);
        setIsDetailsHighlighted(true);

        if (aiDetailsClickTimerRef.current !== null) {
            window.clearTimeout(aiDetailsClickTimerRef.current);
        }

        aiDetailsClickTimerRef.current = window.setTimeout(() => {
            setIsDetailsHighlighted(false);
            aiDetailsClickTimerRef.current = null;
        }, AI_DETAILS_HIGHLIGHT_DURATION_MS);
    };

    const handleHelpfulSelectionChange = (selection: -1 | 1) => {
        const nextSelection = toggleHelpfulSelection(helpfulSelection, selection);

        setHelpfulSelection(nextSelection);
        onHelpfulSelectionChange?.(nextSelection);
    };

    const blurButtonAndRun = (event: React.MouseEvent<HTMLButtonElement>, action?: () => void) => {
        event.currentTarget.blur();
        action?.();
    };

    const helpfulButtonClick = (selection: -1 | 1, event: React.MouseEvent<HTMLButtonElement>) => {
        blurButtonAndRun(event, () => handleHelpfulSelectionChange(selection));
    };

    const revertButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        blurButtonAndRun(event, onRevertToAiValue);
    };
    const shouldRenderTopMessage = detailsTopMessage != null && !isInsufficientDataConfidence;

    return (
        <div className={wrapperClasses}>
            <div className={containerClasses}>
                <AIAnswerMetadataHeader
                    localizer={localizer}
                    isModifiedValue={isModifiedValue}
                    isConfirmed={isConfirmed}
                    isDisabled={isDisabled}
                    isExpanded={isExpanded}
                    isDetailsHighlighted={isDetailsHighlighted}
                    confidenceLabel={confidenceLabel}
                    shouldShowRevertButton={shouldRenderRevertButton}
                    helpfulSelection={helpfulSelection}
                    onToggleDetails={onToggleDetails}
                    onRevertClick={revertButtonClick}
                    onHelpfulClick={helpfulButtonClick}
                />

                {isExpanded &&
                    <AIAnswerMetadataDetails
                        localizer={localizer}
                        reasoning={reasoning}
                        confidenceReasoning={confidenceReasoning}
                        sources={sources}
                    />
                }
            </div>

            {shouldRenderTopMessage &&
                <div className="ai-answer-field__top-message-wrapper">
                    <AIAnswerMetadataTopMessageComponent topMessage={detailsTopMessage} localizer={localizer} />
                </div>
            }
        </div>
    );
};

export default AIAnswerMetadata;
